import { Link, useNavigate } from "@tanstack/react-router";
import { EllipsisVertical, Trash2, TriangleAlert } from "lucide-react";
import type { JSX } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  VisitStatusIcon,
  visitGlyph,
} from "@/components/layout/v3/VisitStatusIcon";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/ui/ConfirmDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PracticeBadge } from "@/components/ui/guided/PracticeVisit";
import { StartVisitButton } from "@/components/ui/visits/StartVisitButton";
import { toast } from "@/hooks/use-toast";
import { useVisitActions } from "@/hooks/useVisitActions";
import { builtInTemplateLabel } from "@/lib/document-name";
import { useFeature } from "@/lib/early-access";
import { matchPracticeVisit } from "@/lib/guided/practice-visits";
import { isPreChartDraft } from "@/lib/pre-chart";
import { getVisitTitle } from "@/lib/utils";
import type { VisitSummary } from "@/services/visits/read-model";
import { useAuthStore } from "@/store/auth.store";
import { usePreChartStore } from "@/store/preChart.store";
import { VisitStatusBadge } from "./VisitStatusBadge";
import {
  formatVisitDuration,
  needsReviewCount,
  parseVisitDate,
  type VisitGroupKey,
} from "./visit-list-model";

// The left column: the time for Today and Yesterday, the weekday for the
// last 7 days and the date for older months (their time moves to the
// subline).
export const visitWhen = (
  date: Date | null,
  group: VisitGroupKey["kind"],
  language: string,
): { column: string; time: string | null } => {
  if (!date) return { column: "—", time: null };
  const time = date.toLocaleTimeString(language, {
    hour: "numeric",
    minute: "2-digit",
  });
  if (group === "today" || group === "yesterday")
    return { column: time, time: null };
  const column = date.toLocaleDateString(
    language,
    group === "lastWeek"
      ? { weekday: "short" }
      : { month: "short", day: "numeric" },
  );
  return { column, time };
};

interface Props {
  visit: VisitSummary;
  group: VisitGroupKey["kind"];
}

// One visit: the whole row opens it; "…" holds Delete (with the same
// confirmation and toasts as before).
export const VisitListRow = ({ visit, group }: Props): JSX.Element => {
  const { t, i18n } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { deleteVisitByFile, result, loading, error } = useVisitActions();
  const title = getVisitTitle(visit.title, visit.rowno);
  const duration = formatVisitDuration(visit.duration);
  const when = visitWhen(
    parseVisitDate(visit.createDate),
    group,
    i18n.language || "en-US",
  );

  useEffect(() => {
    if (result) toast({ variant: "success", description: result });
    if (error) toast({ variant: "error", description: error });
  }, [error, result]);

  const navigate = useNavigate();
  // A pre-chart draft: "Start visit" opens it and starts the recording.
  const draft = isPreChartDraft(visit.status);
  const startVisit = (): void => {
    const visitPublicId = String(visit.public_id);
    usePreChartStore.getState().requestStartVisit(visitPublicId);
    void navigate({
      to: "/visits/$visitPublicId",
      params: { visitPublicId },
    });
  };
  const reviewCount = needsReviewCount(visit);
  // Guided first visit: a practice consulta says so.
  const account = useAuthStore((state) => state.userData?.public_id);
  const guided = useFeature("guided-first-visit");
  const practice =
    guided &&
    matchPracticeVisit(account, {
      public_id: String(visit.public_id),
      title: visit.title,
    });
  const badge = (
    <>
      {practice && <PracticeBadge className="shrink-0" />}
      {reviewCount > 0 && !loading && (
        <span
          className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-v3-warn-tint px-2.5 py-0.5 text-[12px] font-semibold leading-5 text-v3-warn-text"
          data-testid="visit-needs-review"
        >
          <TriangleAlert aria-hidden className="size-3.5" />
          {t("visit.workspace.verify.badge", { count: reviewCount })}
        </span>
      )}
      <VisitStatusBadge deleting={loading} status={visit.status} />
    </>
  );

  return (
    <li className="relative flex items-center font-v3 transition-colors hover:bg-v3-surface-2">
      {/* v3.2 visits list (style.css .vrow): status glyph, title over the
          template, review and status pills, then time and length. */}
      <Link
        className="flex min-h-[62px] min-w-0 flex-1 items-center gap-3.5 py-2.5 pl-4 pr-1 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-v3-brand-ring sm:pl-5"
        data-testid="visit-row"
        params={{ visitPublicId: String(visit.public_id) }}
        to="/visits/$visitPublicId"
      >
        <VisitStatusIcon glyph={visitGlyph(visit)} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14.5px] font-semibold leading-5 text-v3-text">
            {title}
          </span>
          <span className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-v3-text-3">
            {visit.template && (
              <span className="min-w-0 truncate">
                {builtInTemplateLabel(visit.template)}
              </span>
            )}
            {when.time && (
              <span className="whitespace-nowrap">· {when.time}</span>
            )}
            {/* Phones: the time, length and status sit under the title. */}
            <span className="inline-flex items-center gap-2 sm:hidden">
              <span className="tabular-nums">{when.column}</span>
              {badge}
            </span>
          </span>
        </span>
        <span className="hidden items-center gap-2 sm:inline-flex">
          {badge}
        </span>
        <span
          className="w-16 shrink-0 text-right font-mono text-[12.5px] tabular-nums text-v3-text-3 max-sm:hidden"
          data-testid="visit-when"
        >
          {when.column}
        </span>
        <span className="w-11 shrink-0 text-right font-mono text-[12px] tabular-nums text-v3-text-3 max-sm:hidden">
          {duration ?? ""}
        </span>
      </Link>
      {draft && !loading && (
        <StartVisitButton
          className="ml-1 max-sm:hidden"
          size="compact"
          testId="visit-row-start-visit"
          onClick={startVisit}
        />
      )}
      <div className="shrink-0 pr-2 sm:pr-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              aria-label={t("visits.home.moreActions", { title })}
              className="size-11 text-v3-text-3 lg:size-9"
              size="icon"
              type="button"
              variant="ghost"
            >
              <EllipsisVertical />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="min-w-48 **:[[role=menuitem]]:min-h-11 lg:**:[[role=menuitem]]:min-h-9"
          >
            <DropdownMenuItem
              className="gap-3 text-error focus:text-error"
              disabled={loading}
              onSelect={() => {
                setConfirmOpen(true);
              }}
            >
              <Trash2 aria-hidden className="size-4" />
              {t("visits.actions.deleteButton")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <ConfirmDialog
        destructive
        confirmText={t("v3.workspace.deleteVisit")}
        description={t("v3.workspace.deleteThisVisitDescription")}
        open={confirmOpen}
        testId="visit-row-delete-confirm"
        // Names the visit, as before; an untitled visit gets the generic
        // question.
        title={
          visit.title?.trim()
            ? t("v3.workspace.deleteTitle", { title: visit.title.trim() })
            : t("v3.workspace.deleteThisVisit")
        }
        onConfirm={() => {
          void deleteVisitByFile(visit);
        }}
        onOpenChange={setConfirmOpen}
      />
    </li>
  );
};
